import {Injectable} from '@angular/core';
import {Howl} from 'howler';
import {AudioStateService} from './audio-state.service';
import {AudioMobileService} from './audio-mobile.service';

/**
 * Service responsible for audio effects, fade transitions, and volume management
 * Extracted from AudioService to improve maintainability while preserving exact functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AudioEffectsService {

  // Temporary injection point for player service - will be set by facade
  private playerService?: any;

  constructor(
    private stateService: AudioStateService,
    private mobileService: AudioMobileService
  ) {
  }

  // ============================================================================
  // FADE EFFECTS - EXACT preservation from AudioService lines 540-565 & 1042-1110
  // ============================================================================

  /**
   * Fade audio out with exact timing from AudioService lines 540-565
   * @param callback Optional callback to execute when fade completes
   */
  fadeAudioOut(callback?: () => void): void {
    const sound = this.getHowlSound();
    if (!sound) {
      if (callback) callback();
      return;
    }

    console.log(`Starting fade-out effect from volume ${sound.volume()}`);
    const targetVolume = this.stateService.volume();
    const fadeSteps = 10;
    const fadeInterval = 30; // EXACT 30ms intervals
    const volumeDecrement = targetVolume / fadeSteps;
    let currentStep = 0;

    const fadeOutInterval = setInterval(() => {
      currentStep++;
      const newVolume = Math.max(0, targetVolume - (volumeDecrement * currentStep));
      sound.volume(newVolume);

      if (currentStep >= fadeSteps || newVolume <= 0) {
        clearInterval(fadeOutInterval);
        sound.volume(0);
        if (callback) callback();
      }
    }, fadeInterval);
  }

  /**
   * Fade audio in with exact timing from AudioService lines 1042-1110
   * @param keepCurrentVolume Whether to keep current volume for pause/resume operations
   */
  fadeAudioIn(keepCurrentVolume: boolean = false): void {
    const sound = this.getHowlSound();
    if (!sound) return;

    const targetVolume = this.stateService.volume();

    // EXACT logic: Bypass fade for mobile or when keeping current volume
    if (this.mobileService.shouldBypassFade() || keepCurrentVolume) {
      console.log(`Bypassing fade-in: mobile=${this.mobileService.isMobile()}, bypass=${this.mobileService.shouldBypassFade()}, keepVolume=${keepCurrentVolume}`);
      sound.volume(targetVolume);
      this.verifyVolumeIsApplied(sound, targetVolume, 3);
      return;
    }

    console.log(`Starting fade-in effect to volume ${targetVolume}`);

    const fadeSteps = 10;
    const fadeInterval = 50; // EXACT 50ms intervals  
    const volumeIncrement = targetVolume / fadeSteps;
    let currentStep = 0;

    // Start from 0 volume
    sound.volume(0);

    const fadeInInterval = setInterval(() => {
      currentStep++;
      const newVolume = Math.min(targetVolume, volumeIncrement * currentStep);
      sound.volume(newVolume);

      if (currentStep >= fadeSteps || newVolume >= targetVolume) {
        clearInterval(fadeInInterval);
        sound.volume(targetVolume);
        this.verifyVolumeIsApplied(sound, targetVolume, 3);
      }
    }, fadeInterval);
  }

  // ============================================================================
  // VOLUME VERIFICATION - EXACT preservation from AudioService lines 1111-1150
  // ============================================================================

  /**
   * Verify volume is properly applied with minimal retry logic
   * Reduced aggressiveness to prevent audio jitter during volume changes
   * @param sound Howl instance to verify
   * @param targetVolume Target volume to verify
   * @param retryAttempts Number of retry attempts remaining
   */
  verifyVolumeIsApplied(sound: Howl, targetVolume?: number, retryAttempts: number = 1): void {
    if (retryAttempts <= 0) return;

    setTimeout(() => {
      const currentVolume = sound.volume();
      const expectedVolume = targetVolume !== undefined ? targetVolume : this.stateService.volume();

      // Increased tolerance to 0.1 to reduce false positives during slider dragging
      if (Math.abs(currentVolume - expectedVolume) > 0.1) {
        console.log(`Volume mismatch: ${currentVolume} vs ${expectedVolume}, retrying...`);
        sound.volume(expectedVolume);
        // Only one retry to prevent endless loops
        if (retryAttempts > 1) {
          this.verifyVolumeIsApplied(sound, expectedVolume, 1);
        }
      } else {
        console.log(`Volume verification successful: ${currentVolume}`);
      }
    }, 100); // Longer delay to prevent conflicts during rapid volume changes
  }

  // ============================================================================
  // ADVANCED FADE EFFECTS
  // ============================================================================

  /**
   * Cross-fade between tracks for smooth transitions
   * @param newSound New Howl instance to fade in
   * @param callback Optional callback when cross-fade completes
   */
  crossFadeToTrack(newSound: Howl, callback?: () => void): void {
    const oldSound = this.getHowlSound();

    if (!oldSound) {
      this.fadeAudioIn();
      if (callback) callback();
      return;
    }

    // Fade out old, fade in new simultaneously
    this.fadeAudioOut(() => {
      oldSound.stop();
      oldSound.unload();
      this.fadeAudioIn();
      if (callback) callback();
    });
  }

  /**
   * Smooth volume transition over specified duration
   * @param targetVolume Target volume (0-1)
   * @param duration Transition duration in milliseconds
   */
  smoothVolumeTransition(targetVolume: number, duration: number = 200): void {
    const sound = this.getHowlSound();
    if (!sound) return;

    const currentVolume = sound.volume();
    const volumeDiff = targetVolume - currentVolume;
    const steps = 20;
    const stepInterval = duration / steps;
    const stepSize = volumeDiff / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const newVolume = currentVolume + (stepSize * currentStep);
      sound.volume(newVolume);

      if (currentStep >= steps) {
        clearInterval(interval);
        sound.volume(targetVolume);
        this.verifyVolumeIsApplied(sound, targetVolume, 2);
      }
    }, stepInterval);
  }

  /**
   * Apply instant volume change with verification
   * @param volume Volume level (0-1)
   */
  setVolumeInstant(volume: number): void {
    const sound = this.getHowlSound();
    if (!sound) return;

    // Validate volume range
    const clampedVolume = Math.max(0, Math.min(1, volume));

    // Apply volume directly for instant change
    sound.volume(clampedVolume);

    // Verify application
    this.verifyVolumeIsApplied(sound, clampedVolume, 3);
  }

  /**
   * Create a fade-out effect when pausing
   * @param callback Callback to execute when fade completes
   */
  fadeOutOnPause(callback?: () => void): void {
    if (this.mobileService.shouldBypassFade()) {
      // On mobile, just stop immediately
      if (callback) callback();
      return;
    }

    this.fadeAudioOut(callback);
  }

  /**
   * Create a fade-in effect when resuming
   */
  fadeInOnResume(): void {
    if (this.mobileService.shouldBypassFade()) {
      // On mobile, set volume directly
      this.setVolumeInstant(this.stateService.volume());
      return;
    }

    this.fadeAudioIn(true); // Keep current volume for resume
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Set the player service reference (called by facade)
   */
  setPlayerService(playerService: any): void {
    this.playerService = playerService;
  }

  /**
   * Check if any fade effect is currently active
   */
  isFadeActive(): boolean {
    // This can be enhanced to track active fade operations
    return false;
  }

  /**
   * Stop all active fade effects
   */
  stopAllFades(): void {
    // This can be enhanced to stop active fade operations
    // For now, volume verification handles most cases
  }

  /**
   * Get the current Howl sound instance from AudioPlayerService
   */
  private getHowlSound(): Howl | null {
    return this.playerService?.getHowlSound() || null;
  }
} 
